import React, { useEffect } from "react";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { CustomButton, colors } from "@ippon/shared-components";
import {
  useFieldArray,
  Controller,
  type Control,
  type UseFormRegister,
  type UseFormSetValue,
  type FieldErrors,
  type FieldArrayPath,
} from "react-hook-form";
import type { UnitFormValues } from "../UnitTabForm";
import { Can } from "@/components/auth/Can";

interface Props {
  /** 配列フィールドへのパス。例: "private_detail.equipment_specs" */
  name: FieldArrayPath<UnitFormValues>;
  control: Control<UnitFormValues>;
  /** 初期 1 行を dirty を立てずに挿入するために必要 */
  setValue: UseFormSetValue<UnitFormValues>;
  /** EquipmentSpecsInput 自体は Controller で値管理するため、現状未使用 (将来のため受け取るのみ) */
  register?: UseFormRegister<UnitFormValues>;
  errors?: FieldErrors<UnitFormValues>;
  labelWidth?: string;
  inputWidth?: string;
}

const fieldStyle = {
  "& .MuiInputBase-root": {
    height: "34px",
    fontSize: "12px",
    backgroundColor: "#ffffff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#D9D9D9",
      borderRadius: "8px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#D9D9D9" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#D9D9D9 !important",
      borderWidth: "1px",
    },
    "&.Mui-focused": { outline: "none", boxShadow: "none" },
  },
  "& .MuiInputBase-input": {
    fontSize: "12px",
    padding: "0 14px",
    height: "100%",
    "&::placeholder": { color: "#989898", opacity: 0.8 },
  },
} as const;

/**
 * 設置情報 — 項目名 + 台数 の追加方式入力。
 * 物件管理「接道情報」(PropertyOverview の CustomThreeColInputGroup) と
 * 同じ操作感に揃える 2 カラム版。専有部・共用部で共通利用。
 */
const EquipmentSpecsInput: React.FC<Props> = ({
  name,
  control,
  setValue,
  inputWidth,
}) => {
  const { fields, append, remove } = useFieldArray({ control, name });

  // 初期 1 行は setValue (shouldDirty: false) で挿入する。
  // useFieldArray.append は常に dirty を立ててしまい、未編集なのに更新ボタンが
  // 有効化されてしまうため使わない。
  useEffect(() => {
    if (fields.length === 0) {
      setValue(name, [{ label: "", count: "" }] as any, {
        shouldDirty: false,
      });
    }
  }, [fields.length, name, setValue]);

  return (
    <Box sx={{ width: { xs: "100%", sm: inputWidth ?? "100%" }, mb: 2 }}>
      {/* セクションタイトル (1 行目のみ) */}
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: colors.text.primary,
          mb: 1,
        }}
      >
        設置情報
      </Typography>

      {fields.map((field, index) => (
        <Box
          key={field.id}
          display="flex"
          alignItems="end"
          gap={1}
          sx={{ flexWrap: "nowrap", overflow: "hidden", mb: 1 }}
        >
          {/* 項目名 */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              flex: "1 1 0",
            }}
          >
            {index === 0 && (
              <Typography
                fontSize={12}
                color={colors.text.primary}
                fontWeight={700}
                sx={{ mb: 1 }}
              >
                項目名
              </Typography>
            )}
            <Controller
              control={control}
              name={`${name}.${index}.label` as any}
              render={({ field: f }) => (
                <TextField
                  {...f}
                  variant="outlined"
                  size="small"
                  placeholder="例: エアコン"
                  sx={{ width: "100%", ...fieldStyle }}
                />
              )}
            />
          </Box>

          {/* 台数 + 削除ボタン (右端) */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              flex: "1 1 0",
            }}
          >
            {index === 0 && (
              <Typography
                fontSize={12}
                color={colors.text.primary}
                fontWeight={700}
                sx={{ mb: 1 }}
              >
                台数
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Controller
                control={control}
                name={`${name}.${index}.count` as any}
                render={({ field: f }) => (
                  <TextField
                    {...f}
                    variant="outlined"
                    size="small"
                    placeholder="例: 2"
                    inputProps={{ inputMode: "numeric" }}
                    sx={{ width: "100%", ...fieldStyle }}
                  />
                )}
              />
              {index > 0 && (
                <IconButton
                  onClick={() => remove(index)}
                  sx={{ width: "30px", height: "30px", color: "#666" }}
                  title="この行を削除"
                >
                  <ClearIcon sx={{ fontSize: "16px" }} />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
      ))}

      <Box sx={{ mt: 1 }}>
        <Can action="config:edit">
          <CustomButton
            label="＋設置情報を追加"
            buttonCategory="clearPrimary"
            onClick={() => append({ label: "", count: "" })}
            sx={{
              width: { xs: "100%", sm: inputWidth ?? "100%" },
              borderStyle: "dashed",
            }}
          />
        </Can>
      </Box>
    </Box>
  );
};

export default EquipmentSpecsInput;
